import { useMemo, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useDates } from "../../../../dates/DatesContext";
import { useFetch } from "../../../../hooks/api";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import DetailedMetricRow from "../../../core/blocks/DetailedMetricRow";
import PricingIcon from "../../../../assets/images/icons/pricing.svg";
import ThemeLine from "../ThemeLine";
import { keepPreviousData } from "@tanstack/react-query";
import { useAppSelector } from "../../../../redux/hooks";

interface PricingCardProps {
  category?: string | null;
}

const PricingCard = ({ category = null }: PricingCardProps): ReactElement => {
  const { t } = useTranslation(["merchandising"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { start, end, preStart, preEnd, group } = useDates();
  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    filters: "pricing_enrolled",
    ...(category && { category }),
  };

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, isLoading } = useFetch<any>(
    ["productReport", start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"), "pricing_enrolled", category, group],
    `/api/data_report/pricingplan_report`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const metrics = useMemo(() => {
    return [
      {
        ...getMetricSpec("sales"),
        name: ($) => $.metrics["Enrolled Sales"],
        toolTip: ($) => $.metrics["Total Sales from products enrolled in Dynamic Pricing"],
      },
      getMetricSpec("profit"),
      getMetricSpec("units"),
      getMetricSpec("margin"),
    ];
  }, []);

  return (
    <DetailedMetricRow
      title={t(($) => $.merchandising["Dynamic Pricing"])}
      to={`/user/pricing/dashboard`}
      byline={
        <>
          <ThemeLine>{t(($) => $.merchandising["Pricing"])}</ThemeLine>
          {t(($) => $.merchandising["Enroll and manage product pricing"])}
          {isLoading && <i className="fa fa-spinner fa-spin"></i>}
        </>
      }
      totals={{ data: data?.total, loading: isLoading }}
      timeline={{ data: data?.date, loading: isLoading }}
      metrics={metrics}
      icon={PricingIcon}
    />
  );
};

export default PricingCard;
